import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Users, Receipt, BarChart3,
  Gamepad2, UserCog, LogOut, Moon, Sun, Menu, Crown, Sword,
  Shield, Zap, Flame, Star, Trophy, Gem, Target, Coins,
  Sparkles, ChevronRight, Volume2, VolumeX, Settings, Bell
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Gaming Nav Item Component
function GamingNavItem({ icon: Icon, label, path, isActive, onClick, color, powerUp, locked = false, theme = 'dark' }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      onClick={locked ? undefined : onClick}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={locked ? {} : { scale: 1.02, x: 5 }}
      whileTap={locked ? {} : { scale: 0.98 }}
      className={cn(
        "relative w-full p-3 rounded-xl text-left transition-all duration-300 group overflow-hidden border",
        isActive 
          ? theme === 'dark'
            ? "bg-gradient-to-r from-[#9945FF]/20 to-[#00F5FF]/20 border-[#00F5FF]/30"
            : "bg-gradient-to-r from-[#9945FF]/10 to-[#00F5FF]/10 border-[#00F5FF]/40"
          : locked 
          ? "opacity-50 cursor-not-allowed border-transparent"
          : theme === 'dark'
          ? "hover:bg-white/5 border-transparent hover:border-white/10"
          : "hover:bg-gray-100 border-transparent hover:border-gray-200"
      )}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Active indicator line */}
      {isActive && (
        <motion.div
          layoutId="activeIndicator"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full"
          style={{ background: `linear-gradient(180deg, ${color}, transparent)` }}
        />
      )}
      
      {/* Hover glow effect */}
      <AnimatePresence>
        {isHovered && !locked && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 rounded-xl"
            style={{ 
              background: `radial-gradient(circle at left center, ${color}15, transparent 70%)`,
            }}
          />
        )}
      </AnimatePresence>
      
      <div className="relative flex items-center gap-3">
        {/* Icon with gaming border */}
        <div 
          className={cn(
            "relative p-2 rounded-lg transition-all duration-300",
            isActive ? "bg-gradient-to-br" : "bg-white/5 group-hover:bg-white/10"
          )}
          style={isActive ? { 
            background: `linear-gradient(135deg, ${color}30, ${color}10)`,
            boxShadow: `0 0 20px ${color}30`
          } : {}}
        >
          <Icon 
            className={cn(
              "w-5 h-5 transition-all duration-300",
              isActive ? "text-white" : "text-gray-400 group-hover:text-white"
            )} 
            style={isActive ? { color, filter: `drop-shadow(0 0 8px ${color})` } : {}}
          />
          
          {/* Power-up indicator */}
          {powerUp && (
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
              style={{ background: color }}
            >
              <Zap className="w-2 h-2 text-black" />
            </motion.div>
          )}
        </div>
        
        {/* Label */}
        <span className={cn(
          "font-semibold text-sm transition-colors duration-300",
          isActive 
            ? theme === 'dark' ? "text-white" : "text-gray-900"
            : theme === 'dark' ? "text-gray-400 group-hover:text-white" : "text-gray-600 group-hover:text-gray-900"
        )}>
          {label}
        </span>
        
        {/* Locked indicator */}
        {locked && (
          <Shield className="w-4 h-4 text-gray-600 ml-auto" />
        )}
        
        {/* Arrow for active */}
        {isActive && !locked && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="ml-auto"
          >
            <ChevronRight className="w-4 h-4" style={{ color }} />
          </motion.div>
        )}
      </div>
      
      {/* Shine effect on hover */}
      {!locked && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
          initial={{ x: '-100%' }}
          animate={isHovered ? { x: '200%' } : { x: '-100%' }}
          transition={{ duration: 0.6 }}
        />
      )}
    </motion.button>
  );
}

// XP Progress Bar Component
function XPProgressBar({ current, max, level, theme }) {
  const percentage = (current / max) * 100;
  
  return (
    <div className="relative">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={theme === 'dark' ? "text-gray-400" : "text-gray-500"}>Level {level}</span>
        <span className="text-[#00F5FF]">{current}/{max} XP</span>
      </div>
      <div className={cn(
        "h-2 rounded-full overflow-hidden",
        theme === 'dark' ? "bg-[#1a1a2e]" : "bg-gray-200"
      )}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="h-full rounded-full bg-gradient-to-r from-[#9945FF] via-[#00F5FF] to-[#FF00E5]"
          style={{ boxShadow: '0 0 10px #00F5FF' }}
        />
      </div>
    </div>
  );
}

// Gaming Rank Badge Component
function RankBadge({ rank }) {
  const ranks = {
    legendary: { color: '#FFD700', icon: Crown, label: 'LEGENDARY', glow: 'drop-shadow(0 0 10px #FFD700)' },
    epic: { color: '#9945FF', icon: Gem, label: 'EPIC', glow: 'drop-shadow(0 0 10px #9945FF)' },
    rare: { color: '#00F5FF', icon: Star, label: 'RARE', glow: 'drop-shadow(0 0 10px #00F5FF)' },
    common: { color: '#888', icon: Shield, label: 'WARRIOR', glow: 'none' }
  };
  
  const { color, icon: Icon, label, glow } = ranks[rank] || ranks.common;
  
  return (
    <motion.div 
      className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
      style={{ background: `${color}20`, border: `1px solid ${color}40` }}
      whileHover={{ scale: 1.05 }}
    >
      <Icon className="w-3 h-3" style={{ color, filter: glow }} />
      <span className="text-[10px] font-bold" style={{ color }}>{label}</span>
    </motion.div>
  );
}

export function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState(3);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Calculate user rank based on role
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const userRank = isAdmin ? 'legendary' : 'rare';
  const userLevel = isAdmin ? 99 : 25;
  const userXP = isAdmin ? 8500 : 2100;
  const maxXP = isAdmin ? 10000 : 5000;

  const adminLinks = [
    { icon: LayoutDashboard, label: 'Command Center', path: '/', color: '#FFD700', powerUp: true },
    { icon: Sword, label: 'Warriors', path: '/players', color: '#00F5FF' },
    { icon: Coins, label: 'Treasury', path: '/wallet', color: '#FFD700' },
    { icon: Receipt, label: 'Battle Log', path: '/transactions', color: '#FF00E5' },
    { icon: BarChart3, label: 'Intel', path: '/reports', color: '#00FF88' },
  ];
  
  const managementLinks = [
    { icon: UserCog, label: 'Guild Members', path: '/employees', color: '#FF6B00' },
    { icon: Gamepad2, label: 'Arenas', path: '/games', color: '#00F5FF' },
    { icon: BarChart3, label: 'Performance', path: '/reports/performance', color: '#9945FF' },
    ...(user?.role === 'super_admin' ? [{ icon: Crown, label: 'Admin Control', path: '/admin/manage', color: '#FFD700' }] : []),
    { icon: Shield, label: 'System Admin', path: '/admin/system-reset', color: '#FF3366' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Mobile overlay with blur */}
      <AnimatePresence>
        {open && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside 
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-72 transition-transform duration-300 lg:translate-x-0 flex flex-col",
          theme === 'dark' 
            ? "bg-[#0a0a0f] border-r border-white/5"
            : "bg-white border-r border-gray-200 shadow-xl",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        style={theme === 'light' ? { background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' } : {}}
      >
        {/* Animated background effects */}
        {theme === 'dark' && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#9945FF]/10 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#00F5FF]/5 to-transparent" />
            {/* Floating particles */}
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-[#00F5FF]"
                style={{ left: `${20 + i * 15}%`, top: `${30 + i * 10}%` }}
                animate={{ 
                  y: [0, -20, 0], 
                  opacity: [0.2, 0.5, 0.2],
                  scale: [1, 1.5, 1]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 3 + i, 
                  delay: i * 0.5 
                }}
              />
            ))}
          </div>
        )}

        {/* Logo Header */}
        <div className={cn(
          "relative p-5 border-b",
          theme === 'dark' ? "border-white/5" : "border-gray-200"
        )}>
          <motion.div 
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <motion.div 
              className="relative"
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9945FF] to-[#00F5FF] p-[2px]">
                <div className={cn(
                  "w-full h-full rounded-xl flex items-center justify-center",
                  theme === 'dark' ? "bg-[#0a0a0f]" : "bg-white"
                )}>
                  <Gamepad2 className="w-6 h-6 text-[#00F5FF]" />
                </div>
              </div>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute -top-1 -right-1"
              >
                <Sparkles className="w-4 h-4 text-[#FFD700]" />
              </motion.div>
            </motion.div>
            <div>
              <h1 className="text-xl font-black">
                <span className="bg-gradient-to-r from-[#FFD700] via-[#00F5FF] to-[#9945FF] bg-clip-text text-transparent">
                  RIDER
                </span>
              </h1>
              <p className={cn(
                "text-[10px] uppercase tracking-widest",
                theme === 'dark' ? "text-gray-500" : "text-gray-400"
              )}>Operations Hub</p>
            </div>
          </motion.div>
        </div>

        {/* User Profile Card */}
        <div className="relative p-4 mx-4 mt-4 rounded-2xl overflow-hidden">
          {/* Card background */}
          <div className={cn(
            "absolute inset-0 rounded-2xl border",
            theme === 'dark' 
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#0d0d15] border-white/5" 
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-sm"
          )} />
          {theme === 'dark' && <div className="absolute inset-0 hex-pattern opacity-20" />}
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              {/* Gaming Avatar */}
              <div className="relative">
                <motion.div 
                  className={cn(
                    "w-14 h-14 rounded-xl p-[2px]",
                    userRank === 'legendary' && "bg-gradient-to-br from-[#FFD700] to-[#FF6B00]",
                    userRank === 'epic' && "bg-gradient-to-br from-[#9945FF] to-[#FF00E5]",
                    userRank === 'rare' && "bg-gradient-to-br from-[#00F5FF] to-[#0088FF]"
                  )}
                  animate={userRank === 'legendary' ? { 
                    boxShadow: ['0 0 10px #FFD700', '0 0 20px #FFD700', '0 0 10px #FFD700']
                  } : {}}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Avatar className={cn(
                    "w-full h-full rounded-xl border-2",
                    theme === 'dark' ? "border-black" : "border-white"
                  )}>
                    <AvatarFallback className={cn(
                      "text-lg rounded-xl font-bold",
                      theme === 'dark' ? "bg-[#1a1a2e] text-white" : "bg-gray-100 text-gray-800"
                    )}>
                      {user?.username?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </motion.div>
                {/* Online indicator */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className={cn(
                    "absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#00FF88] border-2",
                    theme === 'dark' ? "border-[#0a0a0f]" : "border-white"
                  )}
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-bold truncate",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>{user?.username}</p>
                <RankBadge rank={userRank} />
              </div>
            </div>
            
            {/* XP Progress */}
            <XPProgressBar current={userXP} max={maxXP} level={userLevel} theme={theme} />
            
            {/* Quick Stats */}
            <div className={cn(
              "flex items-center justify-between mt-3 pt-3 border-t",
              theme === 'dark' ? "border-white/5" : "border-gray-200"
            )}>
              <div className="flex items-center gap-1">
                <Trophy className="w-3 h-3 text-[#FFD700]" />
                <span className={cn("text-xs", theme === 'dark' ? "text-gray-400" : "text-gray-500")}>Rank #1</span>
              </div>
              <div className="flex items-center gap-1">
                <Flame className="w-3 h-3 text-[#FF6B00]" />
                <span className={cn("text-xs", theme === 'dark' ? "text-gray-400" : "text-gray-500")}>7 Day Streak</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin">
          {isAdmin && (
            <>
              {/* Main Navigation */}
              <p className={cn(
                "text-[10px] uppercase tracking-widest px-3 mb-2 flex items-center gap-2",
                theme === 'dark' ? "text-gray-600" : "text-gray-400"
              )}>
                <Sword className="w-3 h-3" /> MAIN QUEST
              </p>
              
              {adminLinks.map((link, idx) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <GamingNavItem
                    {...link}
                    isActive={location.pathname === link.path}
                    onClick={() => handleNavClick(link.path)}
                    theme={theme}
                  />
                </motion.div>
              ))}
              
              {/* Management Section */}
              <p className={cn(
                "text-[10px] uppercase tracking-widest px-3 mt-4 mb-2 flex items-center gap-2",
                theme === 'dark' ? "text-gray-600" : "text-gray-400"
              )}>
                <Settings className="w-3 h-3" /> GUILD MANAGEMENT
              </p>
              
              {managementLinks.map((link, idx) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (adminLinks.length + idx) * 0.05 }}
                >
                  <GamingNavItem
                    {...link}
                    isActive={location.pathname === link.path}
                    onClick={() => handleNavClick(link.path)}
                    theme={theme}
                  />
                </motion.div>
              ))}
            </>
          )}
        </nav>

        {/* Bottom Actions */}
        <div className={cn(
          "p-4 border-t space-y-3",
          theme === 'dark' ? "border-white/5" : "border-gray-200"
        )}>
          {/* Quick Actions */}
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setNotifications(0)}
              className={cn(
                "flex-1 p-2.5 rounded-xl border transition-all relative",
                theme === 'dark' 
                  ? "bg-[#1a1a2e] border-white/5 hover:border-[#9945FF]/30"
                  : "bg-white border-gray-200 hover:border-purple-300 shadow-sm"
              )}
            >
              <Bell className={cn(
                "w-4 h-4 mx-auto",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )} />
              {notifications > 0 && (
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FF3366] text-[10px] font-bold flex items-center justify-center text-white"
                >
                  {notifications}
                </motion.span>
              )}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={cn(
                "flex-1 p-2.5 rounded-xl border transition-all",
                theme === 'dark' 
                  ? "bg-[#1a1a2e] border-white/5 hover:border-[#00F5FF]/30"
                  : "bg-white border-gray-200 hover:border-cyan-300 shadow-sm"
              )}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4 text-[#00F5FF] mx-auto" />
              ) : (
                <VolumeX className={cn(
                  "w-4 h-4 mx-auto",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )} />
              )}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className={cn(
                "flex-1 p-2.5 rounded-xl border transition-all",
                theme === 'dark' 
                  ? "bg-[#1a1a2e] border-white/5 hover:border-[#FFD700]/30"
                  : "bg-white border-gray-200 hover:border-amber-300 shadow-sm"
              )}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-[#FFD700] mx-auto" />
              ) : (
                <Moon className="w-4 h-4 text-gray-500 mx-auto" />
              )}
            </motion.button>
          </div>
          
          {/* Logout Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            className={cn(
              "w-full p-3 rounded-xl border transition-all group",
              theme === 'dark' 
                ? "bg-gradient-to-r from-[#FF3366]/10 to-[#FF6B00]/10 border-[#FF3366]/20 hover:border-[#FF3366]/40"
                : "bg-gradient-to-r from-red-50 to-orange-50 border-red-200 hover:border-red-300"
            )}
            data-testid="logout-button"
          >
            <div className="flex items-center justify-center gap-2">
              <LogOut className="w-4 h-4 text-[#FF3366] group-hover:rotate-12 transition-transform" />
              <span className="text-sm font-semibold text-[#FF3366]">Exit Game</span>
            </div>
          </motion.button>
        </div>
      </motion.aside>
    </>
  );
}

export function MobileHeader({ onMenuClick }) {
  const { theme, toggleTheme } = useTheme();
  const [notifications] = useState(3);

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={cn(
        "lg:hidden sticky top-0 z-30 h-16 border-b backdrop-blur-xl flex items-center justify-between px-4",
        theme === 'dark' 
          ? "bg-[#0a0a0f]/90 border-white/5" 
          : "bg-white/90 border-gray-200 shadow-sm"
      )}
    >
      {/* Menu Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={onMenuClick}
        className={cn(
          "p-2.5 rounded-xl border",
          theme === 'dark' 
            ? "bg-[#1a1a2e] border-white/5" 
            : "bg-white border-gray-200 shadow-sm"
        )}
        data-testid="mobile-menu-button"
      >
        <Menu className={cn(
          "h-5 w-5",
          theme === 'dark' ? "text-[#00F5FF]" : "text-cyan-600"
        )} />
      </motion.button>
      
      {/* Logo */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9945FF] to-[#00F5FF] p-[1px]">
          <div className={cn(
            "w-full h-full rounded-lg flex items-center justify-center",
            theme === 'dark' ? "bg-[#0a0a0f]" : "bg-white"
          )}>
            <Gamepad2 className="w-4 h-4 text-[#00F5FF]" />
          </div>
        </div>
        <h1 className="text-lg font-black">
          <span className="bg-gradient-to-r from-[#FFD700] to-[#00F5FF] bg-clip-text text-transparent">
            RIDER
          </span>
        </h1>
      </div>
      
      {/* Right Actions */}
      <div className="flex items-center gap-2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className={cn(
            "relative p-2.5 rounded-xl border",
            theme === 'dark' 
              ? "bg-[#1a1a2e] border-white/5" 
              : "bg-white border-gray-200 shadow-sm"
          )}
        >
          <Bell className={cn(
            "h-4 w-4",
            theme === 'dark' ? "text-gray-400" : "text-gray-500"
          )} />
          {notifications > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FF3366] text-[10px] font-bold flex items-center justify-center text-white">
              {notifications}
            </span>
          )}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleTheme}
          className={cn(
            "p-2.5 rounded-xl border",
            theme === 'dark' 
              ? "bg-[#1a1a2e] border-white/5" 
              : "bg-white border-gray-200 shadow-sm"
          )}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4 text-[#FFD700]" />
          ) : (
            <Moon className="h-4 w-4 text-gray-500" />
          )}
        </motion.button>
      </div>
    </motion.header>
  );
}
